import memoizee from 'memoizee'
import moment from 'moment/moment'

export const setHoursMinutesToDate = (date, hours, minutes) => {
  date.setHours(hours, minutes)
  return date
}

/**
 * Gets initial ranges
 * @param {number} now - For unit tests
 * @returns Object that countains 2 ranges
 * First range: start of the current month - now
 * Second range: start of the previous month - end of the previous month -
 */
export const getDefaultRanges = (now = new Date()) => {
  const currentYear = now.getFullYear()
  const currentMonth = now.getMonth()

  return {
    firstRangeDefault: {
      startDate: setHoursMinutesToDate(
        new Date(currentYear, currentMonth),
        0,
        0,
      ), // First day of the current month
      endDate: setHoursMinutesToDate(now, 23, 59), // Now
    },
    secondRangeDefault: {
      startDate: setHoursMinutesToDate(
        new Date(currentYear, currentMonth - 1, 1),
        0,
        0,
      ), // First day of the previous month
      endDate: setHoursMinutesToDate(
        new Date(currentYear, currentMonth, 0),
        23,
        59,
      ), // Last day of the previous month
    },
  }
}

/**
 * Finds percentage ratio between two values formats it to the 1 decimal and appends % sign
 * @param {number} a
 * @param {number} b
 * @returns {string} Percentage difference between two values
 * @example getPercentageRatio(5, 7) => "71.4%"
 */
const getPercentageRatio = memoizee((a, b) => {
  const result =
    a == 0 && b == 0 ? '0%' : Math.round(Math.abs((100 * a) / b) * 10) / 10

  if (isNaN(result) || result === Infinity) return '0%'

  return result + '%'
})

/**
 * Finds percentage difference between two values formats it to the 1 decimal and appends % sign
 * @param {number} a
 * @param {number} b
 * @returns {string} Percentage difference between two values
 * @example getPercentageDifference(5, 10) => "50%"
 */
const getPercentageDifference = memoizee((a, b) => {
  const fixedNumber = Number(a) - Number(b)
  return `${fixedNumber.toFixed(1)}%`
})

/**
 * Formats date range into readable string
 * @param {string} fromDate
 * @param {string} toDate
 * @returns {string}
 */
const formatRange = (fromDate, toDate) =>
  `${new Date(fromDate).toLocaleDateString()} - ${new Date(
    toDate,
  ).toLocaleDateString()}`

/**
 * Checks if date is in range
 */
const isDateInRange = ({ date, from, to }) => {
  const valueDate = new Date(date).valueOf()
  const valueFrom = new Date(from).valueOf()
  const valueTo = new Date(to).valueOf()
  return valueDate >= valueFrom && valueDate <= valueTo
}

export const formatStats = (firstStats, secondStats, currentStats) => {
  const firstRange = formatRange(firstStats.startDate, firstStats.endDate)
  const secondRange = formatRange(secondStats.startDate, secondStats.endDate)

  // If current date is in any range set that range's totalActiveBAs to the current total active BAs
  if (
    isDateInRange({
      date: new Date(),
      from: firstStats.startDate,
      to: firstStats.endDate,
    })
  ) {
    firstStats.stats.totalActiveBAs = currentStats.brandAmbassadors.totalCount
  }

  if (
    isDateInRange({
      date: new Date(),
      from: secondStats.startDate,
      to: secondStats.endDate,
    })
  ) {
    secondStats.stats.totalActiveBAs = currentStats.brandAmbassadors.totalCount
  }

  return [
    {
      table: {
        name: 'Current active BAs',
        rows: [
          ['Now', 'Total active BAs', currentStats.brandAmbassadors.totalCount],
        ],
      },
    },
    {
      table: {
        name: 'Brand Ambassadors',
        rows: [
          ['KPI Name', firstRange, secondRange, 'Difference'],
          [
            'Total active BAs',
            firstStats.stats.totalActiveBAs,
            secondStats.stats.totalActiveBAs,
            firstStats.stats.totalActiveBAs - secondStats.stats.totalActiveBAs,
          ],
        ],
      },
    },

    {
      table: {
        name: 'Bookings',
        rows: [
          ['KPI Name', firstRange, secondRange, 'Difference'],

          [
            'Total BAs booking missions',
            firstStats.stats.totalActiveBAsThatBooked,
            secondStats.stats.totalActiveBAsThatBooked,
            firstStats.stats.totalActiveBAsThatBooked -
              secondStats.stats.totalActiveBAsThatBooked,
          ],
          [
            'Total BAs booking missions ratio',
            getPercentageRatio(
              firstStats.stats.totalActiveBAsThatBooked,
              firstStats.stats.totalActiveBAs,
            ),
            getPercentageRatio(
              secondStats.stats.totalActiveBAsThatBooked,
              secondStats.stats.totalActiveBAs,
            ),
            getPercentageDifference(
              getPercentageRatio(
                firstStats.stats.totalActiveBAsThatBooked,
                firstStats.stats.totalActiveBAs,
              ).replace('%', ''),
              getPercentageRatio(
                secondStats.stats.totalActiveBAsThatBooked,
                secondStats.stats.totalActiveBAs,
              ).replace('%', ''),
            ),
          ],
          [
            'New BAs booking missions',
            firstStats.stats.totalNewBAsThatBooked,
            secondStats.stats.totalNewBAsThatBooked,
            firstStats.stats.totalNewBAsThatBooked -
              secondStats.stats.totalNewBAsThatBooked,
          ],
          [
            'Total new BAs monthly period',
            firstStats.stats.totalNewBAs,
            secondStats.stats.totalNewBAs,
            firstStats.stats.totalNewBAs - secondStats.stats.totalNewBAs,
          ],
          [
            'New BAs with booked missions ratio',
            getPercentageRatio(
              firstStats.stats.totalNewBAsThatBooked,
              firstStats.stats.totalNewBAs,
            ),
            getPercentageRatio(
              secondStats.stats.totalNewBAsThatBooked,
              secondStats.stats.totalNewBAs,
            ),
            getPercentageDifference(
              getPercentageRatio(
                firstStats.stats.totalNewBAsThatBooked,
                firstStats.stats.totalNewBAs,
              ).replace('%', ''),
              getPercentageRatio(
                secondStats.stats.totalNewBAsThatBooked,
                secondStats.stats.totalNewBAs,
              ).replace('%', ''),
            ),
          ],
        ],
      },
    },
    {
      table: {
        name: 'Completed Missions',
        rows: [
          ['KPI Name', firstRange, secondRange, 'Difference'],

          [
            'Original missions completed',
            firstStats.stats.totalMissionsCompleted,
            secondStats.stats.totalMissionsCompleted,
            firstStats.stats.totalMissionsCompleted -
              secondStats.stats.totalMissionsCompleted,
          ],
          [
            'Total missions pushed',
            firstStats.stats.totalMissionsCreated,
            secondStats.stats.totalMissionsCreated,
            firstStats.stats.totalMissionsCreated -
              secondStats.stats.totalMissionsCreated,
          ],

          [
            'Original missions completed ratio',
            getPercentageRatio(
              firstStats.stats.totalMissionsCompleted,
              firstStats.stats.totalMissionsCreated,
            ),
            getPercentageRatio(
              secondStats.stats.totalMissionsCompleted,
              secondStats.stats.totalMissionsCreated,
            ),
            getPercentageDifference(
              getPercentageRatio(
                firstStats.stats.totalMissionsCompleted,
                firstStats.stats.totalMissionsCreated,
              ).replace('%', ''),
              getPercentageRatio(
                secondStats.stats.totalMissionsCompleted,
                secondStats.stats.totalMissionsCreated,
              ).replace('%', ''),
            ),
          ],
          [
            'Completion ratio',
            getPercentageRatio(
              firstStats.stats.totalMissionsCompleted,
              firstStats.stats.totalMissionsCreated,
            ),
            getPercentageRatio(
              secondStats.stats.totalMissionsCompleted,
              secondStats.stats.totalMissionsCreated,
            ),
            getPercentageDifference(
              getPercentageRatio(
                firstStats.stats.totalMissionsCompleted,
                firstStats.stats.totalMissionsCreated,
              ).replace('%', ''),
              getPercentageRatio(
                secondStats.stats.totalMissionsCompleted,
                secondStats.stats.totalMissionsCreated,
              ).replace('%', ''),
            ),
          ],
        ],
      },
    },
    {
      table: {
        name: 'Spied Missions',
        rows: [
          ['KPI Name', firstRange, secondRange, 'Difference'],
          [
            'Total number of spies shifts completed',
            firstStats.stats.totalSpiedMissions,
            secondStats.stats.totalSpiedMissions,
            firstStats.stats.totalSpiedMissions -
              secondStats.stats.totalSpiedMissions,
          ],
          [
            'Total number of completed missions',
            firstStats.stats.totalMissionsCompleted,
            secondStats.stats.totalMissionsCompleted,
            firstStats.stats.totalMissionsCompleted -
              secondStats.stats.totalMissionsCompleted,
          ],
          [
            'Spied missions ratio',
            getPercentageRatio(
              firstStats.stats.totalSpiedMissions,
              firstStats.stats.totalMissionsCompleted,
            ),
            getPercentageRatio(
              secondStats.stats.totalSpiedMissions,
              secondStats.stats.totalMissionsCompleted,
            ),
            getPercentageDifference(
              getPercentageRatio(
                firstStats.stats.totalSpiedMissions,
                firstStats.stats.totalMissionsCompleted,
              ).replace('%', ''),
              getPercentageRatio(
                secondStats.stats.totalSpiedMissions,
                secondStats.stats.totalMissionsCompleted,
              ).replace('%', ''),
            ),
          ],
        ],
      },
    },
  ]
}

/**
 * Prepare range to sent to API. Set start date to 0 if not exists,
 * set end date to the end of current day if not exists.
 * @param {Object} range - Range to prepare
 * @param {string} [range.startDate] - Range from date.
 * @param {string} [range.endDate] - Range end date.
 * @returns {Object}
 */
export const prepareRange = range => {
  const endDate = moment().set({ hour: 23, minute: 59 })
  return {
    startDate: range.startDate || moment(0),
    endDate: range.endDate || endDate,
  }
}
